import { Action } from 'redux';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { concat, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { storeStateMapper } from '../human-insight/human-insight.state';
import {
    HierarchyUserInfoItem,
    QuickSwitch,
    quickSwitchFilter,
    QuickSwitchItem,
    QuickSwitchType,
    QuickSwitchTypeCriteriaIdTypeMap,
} from '../insight/config.insight.model';
import { legendRetrieveData, rootOverride } from '../insight/insight.action';
import { BaseChainGroup } from '../insight/insight.epic';
import {
    DataFilterOptions,
    FilterContext,
} from '../insight/performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../insight/performance-data/metric.pd.model';
import { Dependencies } from '../store';

import * as actions from './performance-insight.action';

interface ChainGroup<T extends Action<actions.ActionType>>
    extends BaseChainGroup<T> {
    root: T;
    quickSwitches?: QuickSwitch[];
    quickSwitch?: QuickSwitch;
    filteredItems?: QuickSwitchItem[];
    active?: QuickSwitchItem;
    canChangeActive?: boolean;
    initialDataFilterOpts?: DataFilterOptions;
    configUserInfo?: HierarchyUserInfoItem;
}

function getQuickSwitchDefault(
    quickSwitchType: QuickSwitchType,
    context: FilterContext,
): number {
    switch (quickSwitchType) {
        case QuickSwitchType.LOB:
            return context.defaultLobId;
        case QuickSwitchType.REGION:
            return context.defaultRegionId;
        case QuickSwitchType.AREA:
            return context.defaultAreaId;
        case QuickSwitchType.SITE:
            return context.defaultSiteId;
        case QuickSwitchType.PEER_GROUP:
            return context.defaultPeerGroupId;
        case QuickSwitchType.SUPER_GROUP:
            return context.defaultSuperGroupId;
        default:
            return null;
    }
}

function applyUserInfo(
    configUserInfo: HierarchyUserInfoItem,
    dataFilterOpts: DataFilterOptions,
): void {
    if (!configUserInfo || !dataFilterOpts) return;
    dataFilterOpts.lobIdList =
        configUserInfo.lobId == null
            ? dataFilterOpts.lobIdList
            : [configUserInfo.lobId];
    dataFilterOpts.regionIdList =
        configUserInfo.regionId == null
            ? dataFilterOpts.regionIdList
            : [configUserInfo.regionId];
    dataFilterOpts.areaIdList =
        configUserInfo.areaId == null
            ? dataFilterOpts.areaIdList
            : [configUserInfo.areaId];
    dataFilterOpts.siteIdList =
        configUserInfo.siteId == null
            ? dataFilterOpts.siteIdList
            : [configUserInfo.siteId];
    dataFilterOpts.peerGroupIdList =
        configUserInfo.peerGroupId == null
            ? dataFilterOpts.peerGroupIdList
            : [configUserInfo.peerGroupId];
    dataFilterOpts.superGroupIdList =
        configUserInfo.superGroupId == null
            ? dataFilterOpts.superGroupIdList
            : [configUserInfo.superGroupId];
}

function applyQuickSwitchCriteria(
    quickSwitchType: QuickSwitchType,
    initialCriteriaIdType: CriteriaIdType,
    id: number,
    dataFilterOpts: DataFilterOptions,
): void {
    if (!dataFilterOpts) return;
    const ct = QuickSwitchTypeCriteriaIdTypeMap[quickSwitchType];
    if (
        ct === CriteriaIdType.LOB_ID &&
        initialCriteriaIdType === CriteriaIdType.LOB_ID
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.LOB;
        // dataFilterOpts.lobIdList = [...(dataFilterOpts.lobIdList || []), id];
        dataFilterOpts.lobIdList = [id];
    }
    if (
        ct === CriteriaIdType.REGION_ID &&
        initialCriteriaIdType === CriteriaIdType.REGION_ID
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.REGION;
        // dataFilterOpts.regionIdList = [...(dataFilterOpts.regionIdList || []), id];
        dataFilterOpts.regionIdList = [id];
    }
    if (
        ct === CriteriaIdType.AREA_ID &&
        initialCriteriaIdType === CriteriaIdType.AREA_ID
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.AREA;
        // dataFilterOpts.areaIdList = [...(dataFilterOpts.areaIdList || []), id];
        dataFilterOpts.areaIdList = [id];
    }
    if (
        ct === CriteriaIdType.SITE_ID &&
        initialCriteriaIdType === CriteriaIdType.SITE_ID
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.SITE;
        // dataFilterOpts.siteIdList = [...(dataFilterOpts.siteIdList || []), id];
        dataFilterOpts.siteIdList = [id];
    }
    if (
        ct === CriteriaIdType.PEER_GROUP_ID &&
        initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.PEER_GROUP;
        // dataFilterOpts.peerGroupIdList = [...(dataFilterOpts.peerGroupIdList || []), id];
        dataFilterOpts.peerGroupIdList = [id];
    }
    if (
        ct === CriteriaIdType.SUPER_GROUP_ID &&
        initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.SUPER_GROUP;
        // dataFilterOpts.superGroupIdList = [...(dataFilterOpts.superGroupIdList || []), id];
        dataFilterOpts.superGroupIdList = [id];
    }
    if (
        (ct === CriteriaIdType.LOB_ID &&
            initialCriteriaIdType === CriteriaIdType.REGION_ID) ||
        (ct === CriteriaIdType.REGION_ID &&
            initialCriteriaIdType === CriteriaIdType.LOB_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.REGION_LOB;
        if (ct === CriteriaIdType.LOB_ID) {
            // dataFilterOpts.lobIdList = [...(dataFilterOpts.lobIdList || []), id];
            dataFilterOpts.lobIdList = [id];
        } else {
            // dataFilterOpts.regionIdList = [...(dataFilterOpts.regionIdList || []), id];
            dataFilterOpts.regionIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.LOB_ID &&
            initialCriteriaIdType === CriteriaIdType.AREA_ID) ||
        (ct === CriteriaIdType.AREA_ID &&
            initialCriteriaIdType === CriteriaIdType.LOB_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.AREA_LOB;
        if (ct === CriteriaIdType.LOB_ID) {
            // dataFilterOpts.lobIdList = [...(dataFilterOpts.lobIdList || []), id];
            dataFilterOpts.lobIdList = [id];
        } else {
            // dataFilterOpts.areaIdList = [...(dataFilterOpts.areaIdList || []), id];
            dataFilterOpts.areaIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.LOB_ID &&
            initialCriteriaIdType === CriteriaIdType.SITE_ID) ||
        (ct === CriteriaIdType.SITE_ID &&
            initialCriteriaIdType === CriteriaIdType.LOB_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.SITE_LOB;
        if (ct === CriteriaIdType.LOB_ID) {
            // dataFilterOpts.lobIdList = [...(dataFilterOpts.lobIdList || []), id];
            dataFilterOpts.lobIdList = [id];
        } else {
            // dataFilterOpts.siteIdList = [...(dataFilterOpts.siteIdList || []), id];
            dataFilterOpts.siteIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.PEER_GROUP_ID &&
            initialCriteriaIdType === CriteriaIdType.REGION_ID) ||
        (ct === CriteriaIdType.REGION_ID &&
            initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.REGION_PEER_GROUP;
        if (ct === CriteriaIdType.PEER_GROUP_ID) {
            // dataFilterOpts.peerGroupIdList = [...(dataFilterOpts.peerGroupIdList || []), id];
            dataFilterOpts.peerGroupIdList = [id];
        } else {
            // dataFilterOpts.regionIdList = [...(dataFilterOpts.regionIdList || []), id];
            dataFilterOpts.regionIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.PEER_GROUP_ID &&
            initialCriteriaIdType === CriteriaIdType.AREA_ID) ||
        (ct === CriteriaIdType.AREA_ID &&
            initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID)
    ) {
        dataFilterOpts.viewType = PerformanceDataViewType.AREA_PEER_GROUP;
        if (ct === CriteriaIdType.PEER_GROUP_ID) {
            // dataFilterOpts.peerGroupIdList = [...(dataFilterOpts.peerGroupIdList || []), id];
            dataFilterOpts.peerGroupIdList = [id];
        } else {
            // dataFilterOpts.areaIdList = [...(dataFilterOpts.areaIdList || []), id];
            dataFilterOpts.areaIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.PEER_GROUP_ID &&
            initialCriteriaIdType === CriteriaIdType.SITE_ID) ||
        (ct === CriteriaIdType.SITE_ID &&
            initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.SITE_PEER_GROUP;
        if (ct === CriteriaIdType.PEER_GROUP_ID) {
            // dataFilterOpts.peerGroupIdList = [...(dataFilterOpts.peerGroupIdList || []), id];
            dataFilterOpts.peerGroupIdList = [id];
        } else {
            // dataFilterOpts.siteIdList = [...(dataFilterOpts.siteIdList || []), id];
            dataFilterOpts.siteIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.SUPER_GROUP_ID &&
            initialCriteriaIdType === CriteriaIdType.REGION_ID) ||
        (ct === CriteriaIdType.REGION_ID &&
            initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.REGION_SUPER_GROUP;
        if (ct === CriteriaIdType.SUPER_GROUP_ID) {
            // dataFilterOpts.superGroupIdList = [...(dataFilterOpts.superGroupIdList || []), id];
            dataFilterOpts.superGroupIdList = [id];
        } else {
            // dataFilterOpts.regionIdList = [...(dataFilterOpts.regionIdList || []), id];
            dataFilterOpts.regionIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.SUPER_GROUP_ID &&
            initialCriteriaIdType === CriteriaIdType.AREA_ID) ||
        (ct === CriteriaIdType.AREA_ID &&
            initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.AREA_SUPER_GROUP;
        if (ct === CriteriaIdType.SUPER_GROUP_ID) {
            // dataFilterOpts.superGroupIdList = [...(dataFilterOpts.superGroupIdList || []), id];
            dataFilterOpts.superGroupIdList = [id];
        } else {
            // dataFilterOpts.areaIdList = [...(dataFilterOpts.areaIdList || []), id];
            dataFilterOpts.areaIdList = [id];
        }
    }
    if (
        (ct === CriteriaIdType.SUPER_GROUP_ID &&
            initialCriteriaIdType === CriteriaIdType.SITE_ID) ||
        (ct === CriteriaIdType.SITE_ID &&
            initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID)
    ) {
        // dataFilterOpts.viewType = PerformanceDataViewType.SITE_SUPER_GROUP;
        if (ct === CriteriaIdType.SUPER_GROUP_ID) {
            // dataFilterOpts.superGroupIdList = [...(dataFilterOpts.superGroupIdList || []), id];
            dataFilterOpts.superGroupIdList = [id];
        } else {
            // dataFilterOpts.siteIdList = [...(dataFilterOpts.siteIdList || []), id];
            dataFilterOpts.siteIdList = [id];
        }
    }
}

function levelOptionSelect<TAction extends Action<actions.ActionType>>(
    action: actions.LevelOptionSelectAction,
    deps: Dependencies,
    group: ChainGroup<TAction>,
): Observable<ChainGroup<TAction>> {
    group.active = action.opt;
    let initialCriteria = null; // this'll remain null if !quickSwitches || !quickSwitches.items || initialCriteriaIdTypeArg == null || initialCriteriaIdTypeArg is not found in quickSwitches.items
    if (group.quickSwitches) {
        // TODO handle multiple quickswitches?
        group.quickSwitch =
            group.quickSwitches.length !== 0 && group.quickSwitches[0];
        group.filteredItems = quickSwitchFilter(
            group.quickSwitch,
            group.viewContext,
        ); // TODO revise. this'll be based on what ROOT selects
        if (
            group.filteredItems &&
            group.filteredItems.length !== 0 &&
            (group.active == null ||
                !group.filteredItems.some(
                    (e) => e.initialCriteria === group.active.initialCriteria,
                ))
        ) {
            if (group.viewContext) {
                initialCriteria = getQuickSwitchDefault(
                    group.quickSwitch.quickSwitchType,
                    group.viewContext,
                );
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                const initialCriteriaStr = `${initialCriteria}`;
                group.active = group.filteredItems.find(
                    (e) => e.initialCriteria === initialCriteriaStr,
                );
            }
            if (!group.active) {
                group.active = group.filteredItems[0];
            }
        }
        if (
            !group.active &&
            group.quickSwitch &&
            group.quickSwitch.quickSwitchType === QuickSwitchType.MARKET
        ) {
            group.active = {
                name: QuickSwitchType.MARKET,
                shortName: null,
                initials: null,
                displayOrder: null,
                initialCriteria: null,
                icon: null,
                view: null,
                path: null,
                dateRanges: [],
                extra: null,
            };
        }
    }
    group.canChangeActive =
        !group.viewContext.lockToUserProfile &&
        group.quickSwitch &&
        group.quickSwitch.quickSwitchType !== QuickSwitchType.MARKET &&
        initialCriteria == null;
    if (group.viewContext.lockToUserProfile) {
        applyUserInfo(group.configUserInfo, group.initialDataFilterOpts);
    }
    if (group.quickSwitch && group.config && group.active) {
        applyQuickSwitchCriteria(
            group.quickSwitch.quickSwitchType,
            group.config.initialCriteriaIdType,
            parseInt(group.active.initialCriteria),
            group.initialDataFilterOpts,
        );
    }
    return of(group);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const levelOptionSelectLevelOption = (
    action: ActionsObservable<actions.LevelOptionSelectAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.PERFORMANCE_INSIGHT__LEVEL_OPTION__SELECT),
        switchMap((act) => {
            const rootState = storeStateMapper(state.value);

            return of<ChainGroup<actions.LevelOptionSelectAction>>({
                root: act,
                quickSwitches: rootState.configs[rootState.activeConfig]
                    ? rootState.configs[rootState.activeConfig].quickSwitches
                    : null,
                viewContext: rootState.viewContext,
                config: rootState.configs[rootState.activeConfig],
                initialDataFilterOpts: rootState.initialDataFilterOpts
                    ? JSON.parse(
                          JSON.stringify(rootState.initialDataFilterOpts),
                      )
                    : null,
                configUserInfo: rootState.configUserInfo,
            }).pipe(
                mergeMap((group) => levelOptionSelect(act, deps, group)),
                mergeMap((group) =>
                    concat(
                        of(
                            rootOverride(
                                null,
                                null,
                                null,
                                null,
                                group.initialDataFilterOpts,
                            ),
                        ),
                        of(
                            actions.levelOptionSelectResult(
                                group.quickSwitch,
                                group.filteredItems,
                                group.active,
                                group.canChangeActive,
                            ),
                        ),
                        of(legendRetrieveData()),
                    ),
                ),
                catchError(() => of(actions.levelOptionSelectResult())),
            );
        }),
    );

export default [levelOptionSelectLevelOption];
